import moment from 'moment'
import type { IntlShape } from 'react-intl'
import { INTL_SHORT_DAY_NAME_DAY_MONTH } from 'constants/dateFormats'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import { intlDateFormatter } from 'lib/datetime/dateUtils'
import { formatShortDateRange } from 'tripPlanner/utils'

export function formatDayLabel(dateStr: string, intl: IntlShape = getCurrentIntl()): string {
  return intlDateFormatter(dateStr, INTL_SHORT_DAY_NAME_DAY_MONTH, intl.locale)
}

export function computeStayDateRange(dayDate: string, dayOfStay: number, totalDays: number): string {
  const stayStart = moment(dayDate).subtract(dayOfStay - 1, 'days')
  const stayEnd = stayStart.clone().add(totalDays - 1, 'days')
  return formatShortDateRange({ startDate: stayStart, endDate: stayEnd, includeYear: false })
}

/**
 * Returns a date-aware day label without message translation.
 * Date formatting still follows the current locale.
 */
export function getDayLabel(dayNumber: number, dateStr?: string): string {
  return getLocalisedDayLabel(getCurrentIntl(), dayNumber, dateStr)
}

/**
 * Returns a date-aware day label with i18n support. Use for all
 * user-facing UI text rendered inside React components.
 */
export function getLocalisedDayLabel(intl: IntlShape, dayNumber: number, dateStr?: string): string {
  if (dateStr) return formatDayLabel(dateStr, intl)
  return intl.formatMessage(
    {
      id: 'nKq1zQQctl_T9caEJO6m',
      defaultMessage: 'Day {dayNumber}',
      description: 'Label for a specific day number in the trip planner itinerary',
    },
    { dayNumber },
  )
}

export function computeTripDatesFromStops(
  stops: ReadonlyArray<{ startDate?: string; endDate?: string }> | undefined,
): { startDate: string; endDate: string } | undefined {
  if (!stops) return undefined

  let minStart: string | undefined
  let maxEnd: string | undefined

  for (const stop of stops) {
    if (!stop.startDate || !stop.endDate) continue
    if (!minStart || stop.startDate < minStart) minStart = stop.startDate
    if (!maxEnd || stop.endDate > maxEnd) maxEnd = stop.endDate
  }

  if (!minStart || !maxEnd) return undefined
  return { startDate: minStart, endDate: maxEnd }
}
